import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Sparkles, Upload, FileText, Image as ImageIcon,
  Loader2, Eye, DollarSign, Receipt, AlertTriangle, Clock, Mail, Copy,
  Filter, CheckCircle, XCircle,
} from "lucide-react";

const STATUS_STYLES = {
  draft:             { label: "Draft",            className: "bg-gray-50 text-gray-600 border-gray-200" },
  awaiting_payment:  { label: "Awaiting Payment", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  partial_payment:   { label: "Partial",          className: "bg-blue-50 text-blue-700 border-blue-200" },
  paid:              { label: "Paid",             className: "bg-green-50 text-green-700 border-green-200" },
  overdue:           { label: "Overdue",          className: "bg-red-50 text-red-700 border-red-200" },
};

const fmtCurrency = (v) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(v || 0);

export default function BillsPage() {
  const { token } = useAuth();
  const [bills, setBills] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [inboundAddress, setInboundAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const fileInputRef = useRef(null);

  const [paymentDialog, setPaymentDialog] = useState({ open: false, bill: null });
  const [paymentForm, setPaymentForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    method: "bank_transfer",
    reference: "",
    notes: "",
  });
  const [previewBill, setPreviewBill] = useState(null);

  const emptyForm = {
    supplier_name: "",
    supplier_email: "",
    bill_number: "",
    issue_date: new Date().toISOString().split("T")[0],
    due_date: "",
    amount: "",
    tax: "",
    total: "",
    payment_terms: "30",
    account_code: "",
    notes: "",
  };
  const [formData, setFormData] = useState(emptyForm);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const [billsRes, accRes, sumRes, inAddrRes] = await Promise.all([
        axios.get(`${API}/bills`, { headers, params }),
        axios.get(`${API}/chart-of-accounts`, { headers, params: { type: "Expense", active_only: true } }),
        axios.get(`${API}/bills/dashboard-summary`, { headers }),
        axios.get(`${API}/bills/inbound-address`, { headers }),
      ]);
      setBills(billsRes.data || []);
      setAccounts(accRes.data || []);
      setSummary(sumRes.data || null);
      setInboundAddress(inAddrRes.data || null);
    } catch {
      toast.error("Failed to load bills");
    } finally { setLoading(false); }
  }, [headers, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => {
    setEditing(null);
    setAttachmentPreview(null);
    setFormData({ ...emptyForm, issue_date: new Date().toISOString().split("T")[0] });
  };

  const handleScan = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/bills/extract`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      setFormData((prev) => ({
        ...prev,
        supplier_name: d.supplier_name || prev.supplier_name,
        supplier_email: d.supplier_email || prev.supplier_email,
        bill_number: d.bill_number || prev.bill_number,
        issue_date: d.issue_date || prev.issue_date,
        due_date: d.due_date || prev.due_date,
        amount: d.amount ? String(d.amount) : prev.amount,
        tax: d.tax_amount != null ? String(d.tax_amount) : prev.tax,
        total: d.total ? String(d.total) : prev.total,
      }));
      setAttachmentPreview({
        base64: d.attachment_base64,
        mime: d.attachment_mime,
        filename: d.attachment_filename,
      });
      toast.success("Bill scanned — please review the details");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extract bill");
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        supplier_name: formData.supplier_name,
        supplier_email: formData.supplier_email || null,
        bill_number: formData.bill_number || null,
        issue_date: formData.issue_date || null,
        due_date: formData.due_date || null,
        amount: parseFloat(formData.amount) || 0,
        tax: parseFloat(formData.tax) || 0,
        total: formData.total ? parseFloat(formData.total) : null,
        payment_terms: parseInt(formData.payment_terms) || 30,
        account_code: formData.account_code || null,
        notes: formData.notes || null,
        attachment_base64: attachmentPreview?.base64 || editing?.attachment_base64 || null,
        attachment_mime: attachmentPreview?.mime || editing?.attachment_mime || null,
        attachment_filename: attachmentPreview?.filename || editing?.attachment_filename || null,
      };
      if (editing) {
        await axios.put(`${API}/bills/${editing.id}`, payload, { headers });
        toast.success("Bill updated");
      } else {
        await axios.post(`${API}/bills`, payload, { headers });
        toast.success("Bill created");
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save bill");
    }
  };

  const handleEdit = (b) => {
    setEditing(b);
    setFormData({
      supplier_name: b.supplier_name || "",
      supplier_email: b.supplier_email || "",
      bill_number: b.bill_number || "",
      issue_date: b.issue_date || "",
      due_date: b.due_date || "",
      amount: b.amount?.toString() || "",
      tax: b.tax?.toString() || "",
      total: b.total?.toString() || "",
      payment_terms: String(b.payment_terms ?? 30),
      account_code: b.account_code || "",
      notes: b.notes || "",
    });
    setAttachmentPreview(b.attachment_base64 ? {
      base64: b.attachment_base64, mime: b.attachment_mime, filename: b.attachment_filename,
    } : null);
    setDialogOpen(true);
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete bill from "${b.supplier_name}"?`)) return;
    try {
      await axios.delete(`${API}/bills/${b.id}`, { headers });
      toast.success("Bill deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const openPaymentDialog = (b) => {
    setPaymentDialog({ open: true, bill: b });
    setPaymentForm({
      date: new Date().toISOString().split("T")[0],
      amount: b.outstanding_balance?.toString() || "",
      method: "bank_transfer",
      reference: "",
      notes: "",
    });
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const b = paymentDialog.bill;
    try {
      const payload = {
        date: paymentForm.date,
        amount: parseFloat(paymentForm.amount) || 0,
        method: paymentForm.method,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null,
      };
      if (payload.amount <= 0) {
        toast.error("Amount must be > 0");
        return;
      }
      if (payload.amount > (b.outstanding_balance || 0) + 0.005) {
        const ok = window.confirm(
          `This payment (${payload.amount}) exceeds the outstanding balance (${b.outstanding_balance}). Continue anyway?`
        );
        if (!ok) return;
      }
      await axios.post(`${API}/bills/${b.id}/payments`, payload, { headers });
      toast.success("Payment recorded");
      setPaymentDialog({ open: false, bill: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    }
  };

  const deletePayment = async (bill, paymentId) => {
    if (!window.confirm("Remove this payment?")) return;
    try {
      await axios.delete(`${API}/bills/${bill.id}/payments/${paymentId}`, { headers });
      toast.success("Payment removed");
      // Refresh both list and the previewBill view
      const fresh = await axios.get(`${API}/bills/${bill.id}`, { headers });
      setPreviewBill(fresh.data);
      fetchAll();
    } catch { toast.error("Failed to remove payment"); }
  };

  const copyInboundAddress = () => {
    if (!inboundAddress?.address) return;
    try {
      navigator.clipboard.writeText(inboundAddress.address).then(
        () => toast.success("Email address copied"),
        () => toast.error("Couldn't copy — please copy manually")
      );
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const attachmentDataUrl = (b) => (b?.attachment_base64
    ? `data:${b.attachment_mime || "application/pdf"};base64,${b.attachment_base64}`
    : null);

  return (
    <DashboardLayout title="Bills (Supplier Invoices)">
      <div className="space-y-6" data-testid="bills-page">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-hover border-l-4" style={{ borderLeftColor: "#FFB347" }}>
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Payable</p>
              <p className="text-2xl font-bold mt-1" style={{ color: "#C77E0F" }} data-testid="bills-total-payable">
                {fmtCurrency(summary?.total_payable)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{summary?.bill_count || 0} bills total</p>
            </CardContent>
          </Card>
          <Card className="card-hover border-l-4" style={{ borderLeftColor: "#FF6B6B" }}>
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Overdue</p>
              <p className="text-2xl font-bold text-red-600 mt-1" data-testid="bills-overdue">
                {fmtCurrency(summary?.overdue_amount)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{summary?.overdue_count || 0} bills</p>
            </CardContent>
          </Card>
          <Card className="card-hover border-l-4" style={{ borderLeftColor: "#5C8FCF" }}>
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Due in 7 Days</p>
              <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="bills-upcoming">
                {fmtCurrency(summary?.upcoming_amount)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{summary?.upcoming_count || 0} bills</p>
            </CardContent>
          </Card>
          <Card className="card-hover border-l-4" style={{ borderLeftColor: "#2E5C46" }}>
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Paid (All time)</p>
              <p className="text-2xl font-bold text-primary mt-1" data-testid="bills-paid-total">
                {fmtCurrency(summary?.total_paid)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">across all bills</p>
            </CardContent>
          </Card>
        </div>

        {/* Inbound email banner */}
        {inboundAddress?.address && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start sm:items-center gap-3 flex-col sm:flex-row sm:justify-between">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="text-sm font-medium">Forward supplier invoices to this address — they&apos;ll be auto-parsed into Bills.</p>
                    <p className="text-base font-mono text-primary mt-1" data-testid="inbound-email-address">{inboundAddress.address}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Requires one-time DNS setup (MX → mx.sendgrid.net) + SendGrid Inbound Parse webhook configuration.
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={copyInboundAddress} data-testid="copy-inbound-address-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter + Add */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="bills-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                <SelectItem value="partial_payment">Partial Payment</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-bill-btn">
                <Plus className="w-4 h-4 mr-2" />Add Bill
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Bill" : "Add Supplier Bill"}</DialogTitle>
                <DialogDescription>Upload the bill PDF to auto-fill, or enter details manually.</DialogDescription>
              </DialogHeader>

              {/* Scan */}
              <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Scan bill with AI</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    ref={fileInputRef} type="file"
                    accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={(e) => handleScan(e.target.files?.[0])}
                    data-testid="bill-scan-input"
                  />
                  <Button
                    type="button" variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={extracting}
                    data-testid="scan-bill-btn"
                  >
                    {extracting
                      ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</>)
                      : (<><Upload className="w-4 h-4 mr-2" />Upload Bill PDF</>)}
                  </Button>
                  {attachmentPreview && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {attachmentPreview.mime?.includes("pdf")
                        ? <FileText className="w-4 h-4" />
                        : <ImageIcon className="w-4 h-4" />}
                      <span className="truncate max-w-[200px]">{attachmentPreview.filename}</span>
                      <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs"
                        onClick={() => setAttachmentPreview(null)}>
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">PDF or image, max 10MB. Auto-extracts supplier, bill #, dates, amounts.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier *</Label>
                    <Input data-testid="bill-supplier-input"
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      required />
                  </div>
                  <div className="space-y-2">
                    <Label>Supplier Email</Label>
                    <Input value={formData.supplier_email}
                      onChange={(e) => setFormData({ ...formData, supplier_email: e.target.value })}
                      placeholder="billing@supplier.com" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Bill Number</Label>
                    <Input value={formData.bill_number}
                      onChange={(e) => setFormData({ ...formData, bill_number: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Issue Date *</Label>
                    <Input type="date" value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                      required />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input type="date" value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Amount (excl. tax) *</Label>
                    <Input type="number" step="0.01" data-testid="bill-amount-input"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required />
                  </div>
                  <div className="space-y-2">
                    <Label>GST / Tax</Label>
                    <Input type="number" step="0.01"
                      value={formData.tax}
                      onChange={(e) => setFormData({ ...formData, tax: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Total (override)</Label>
                    <Input type="number" step="0.01"
                      placeholder="Auto = amount + tax"
                      value={formData.total}
                      onChange={(e) => setFormData({ ...formData, total: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payment Terms (days)</Label>
                    <Input type="number" min="0"
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Account (COA)</Label>
                    <Select
                      value={formData.account_code || "__none__"}
                      onValueChange={(v) => setFormData({ ...formData, account_code: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="__none__">— None —</SelectItem>
                        {accounts.map((a) => (
                          <SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-bill-btn">{editing ? "Update" : "Save"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Bills table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground animate-pulse">Loading...</div>
            ) : bills.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Receipt className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No bills yet</h3>
                <p className="text-muted-foreground mt-1">Add your first supplier bill or forward one to your inbound address.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header">Supplier</TableHead>
                    <TableHead className="table-header">Bill #</TableHead>
                    <TableHead className="table-header">Issued</TableHead>
                    <TableHead className="table-header">Due</TableHead>
                    <TableHead className="table-header text-right">Total</TableHead>
                    <TableHead className="table-header text-right">Paid</TableHead>
                    <TableHead className="table-header text-right">Outstanding</TableHead>
                    <TableHead className="table-header">Status</TableHead>
                    <TableHead className="table-header text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((b) => {
                    const s = STATUS_STYLES[b.status] || STATUS_STYLES.draft;
                    return (
                      <TableRow key={b.id} className="hover:bg-accent/50" data-testid={`bill-row-${b.id}`}>
                        <TableCell className="font-medium">
                          {b.supplier_name}
                          {b.source === "email" && (
                            <Badge variant="outline" className="ml-2 text-xs"><Mail className="w-3 h-3 mr-1" />email</Badge>
                          )}
                        </TableCell>
                        <TableCell>{b.bill_number || "-"}</TableCell>
                        <TableCell className="data-cell">{b.issue_date || "-"}</TableCell>
                        <TableCell className="data-cell">
                          {b.due_date || "-"}
                          {b.status === "overdue" && (
                            <AlertTriangle className="inline w-3 h-3 ml-1 text-red-500" />
                          )}
                        </TableCell>
                        <TableCell className="text-right data-cell">{fmtCurrency(b.total)}</TableCell>
                        <TableCell className="text-right data-cell text-green-700">{fmtCurrency(b.paid_amount)}</TableCell>
                        <TableCell className="text-right data-cell font-medium">{fmtCurrency(b.outstanding_balance)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={s.className}>{s.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {b.outstanding_balance > 0 && (
                              <Button variant="ghost" size="icon" onClick={() => openPaymentDialog(b)} title="Record payment" data-testid={`bill-pay-${b.id}`}>
                                <DollarSign className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => setPreviewBill(b)} title="View" data-testid={`bill-view-${b.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(b)} data-testid={`bill-edit-${b.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(b)}
                              data-testid={`bill-delete-${b.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(o) => !o && setPaymentDialog({ open: false, bill: null })}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {paymentDialog.bill && (
                <>For &quot;{paymentDialog.bill.supplier_name}&quot; · Outstanding: {fmtCurrency(paymentDialog.bill.outstanding_balance)}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPayment} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" required value={paymentForm.date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                  data-testid="payment-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" step="0.01" required value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  data-testid="payment-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Method</Label>
                <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bpay">BPAY</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="Transaction ID / cheque #" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialog({ open: false, bill: null })}>Cancel</Button>
              <Button type="submit" data-testid="record-payment-btn">Record Payment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewBill} onOpenChange={(o) => !o && setPreviewBill(null)}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewBill?.supplier_name}</DialogTitle>
            <DialogDescription>
              Bill #{previewBill?.bill_number || "-"} · {previewBill?.issue_date}
            </DialogDescription>
          </DialogHeader>
          {previewBill && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Total:</span> <span className="font-medium">{fmtCurrency(previewBill.total)}</span></div>
                <div><span className="text-muted-foreground">Paid:</span> <span className="font-medium text-green-700">{fmtCurrency(previewBill.paid_amount)}</span></div>
                <div><span className="text-muted-foreground">Outstanding:</span> <span className="font-medium">{fmtCurrency(previewBill.outstanding_balance)}</span></div>
                <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className={STATUS_STYLES[previewBill.status]?.className}>{STATUS_STYLES[previewBill.status]?.label}</Badge></div>
                <div><span className="text-muted-foreground">Due:</span> {previewBill.due_date || "-"}</div>
                <div><span className="text-muted-foreground">Account:</span> {previewBill.account_code || "-"}</div>
              </div>

              {/* Payments table */}
              {(previewBill.payments?.length || 0) > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Payments</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="table-header">Date</TableHead>
                        <TableHead className="table-header">Method</TableHead>
                        <TableHead className="table-header">Reference</TableHead>
                        <TableHead className="table-header text-right">Amount</TableHead>
                        <TableHead className="table-header text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewBill.payments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.date}</TableCell>
                          <TableCell><Badge variant="outline">{p.method || "-"}</Badge></TableCell>
                          <TableCell className="text-muted-foreground">{p.reference || "-"}</TableCell>
                          <TableCell className="text-right data-cell">{fmtCurrency(p.amount)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                              onClick={() => deletePayment(previewBill, p.id)} data-testid={`delete-payment-${p.id}`}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Attachment */}
              {previewBill.attachment_base64 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Attachment</h4>
                  {previewBill.attachment_mime?.includes("pdf") ? (
                    <iframe title="bill-pdf" src={attachmentDataUrl(previewBill)} className="w-full h-[500px] border rounded" />
                  ) : (
                    <img src={attachmentDataUrl(previewBill)} alt="Bill" className="w-full max-h-[60vh] object-contain rounded border" />
                  )}
                </div>
              )}

              {previewBill.notes && (
                <p className="text-sm text-muted-foreground italic">{previewBill.notes}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
